import { useState, useEffect, useCallback } from 'react';
import { Goal } from '../types';
import { apiService } from '../services/api';

export const useGoals = (projectId: string | null) => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGoals = useCallback(async (params?: {
    includeHierarchy?: boolean;
    includeMetrics?: boolean;
  }) => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getGoals(projectId, params);
      setGoals(response.goals);
    } catch (err) {
      console.error('Failed to fetch goals:', err);
      setError('Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchGoals({ includeHierarchy: true });
    } else {
      setGoals([]);
      setLoading(false);
    }
  }, [projectId, fetchGoals]);

  const createGoal = async (goalData: {
    parentGoalId?: string;
    name: string;
    description?: string;
    estimatedHours?: number;
    targetDate?: string;
    complexityScore?: number;
    standalonePotential?: boolean;
  }): Promise<Goal | null> => {
    if (!projectId) return null;

    try {
      const response = await apiService.createGoal({
        projectId,
        ...goalData
      });
      
      // Refresh goals to maintain hierarchy
      await fetchGoals({ includeHierarchy: true });
      
      return response.goal;
    } catch (err) {
      console.error('Failed to create goal:', err);
      setError('Failed to create goal');
      return null;
    }
  };

  const updateGoal = async (id: string, updates: {
    name?: string;
    description?: string;
    estimatedHours?: number;
    targetDate?: string;
    status?: string;
    progress?: number;
    complexityScore?: number;
    standalonePotential?: boolean;
  }): Promise<boolean> => {
    try {
      const response = await apiService.updateGoal(id, updates);
      
      // Update goals in state
      setGoals(prev => updateGoalInTree(prev, id, response.goal));
      
      return true;
    } catch (err) {
      console.error('Failed to update goal:', err);
      setError('Failed to update goal');
      return false;
    }
  };

  const deleteGoal = async (id: string): Promise<boolean> => {
    try {
      await apiService.deleteGoal(id);
      
      // Remove goal from state
      setGoals(prev => removeGoalFromTree(prev, id));
      
      return true;
    } catch (err) {
      console.error('Failed to delete goal:', err);
      setError('Failed to delete goal');
      return false;
    }
  };

  const extractGoal = async (id: string, newProjectName: string): Promise<boolean> => {
    try {
      await apiService.extractGoal(id, { newProjectName });
      // Note: The goal remains in current project, but a new project is created
      return true;
    } catch (err) {
      console.error('Failed to extract goal:', err);
      setError('Failed to extract goal');
      return false;
    }
  };

  const refreshGoals = () => {
    fetchGoals({ includeHierarchy: true });
  };

  return {
    goals,
    loading,
    error,
    createGoal,
    updateGoal,
    deleteGoal,
    extractGoal,
    refreshGoals,
    refetch: fetchGoals
  };
};

// Helper function to update a goal in the tree structure
function updateGoalInTree(goals: Goal[], goalId: string, updatedGoal: Goal): Goal[] {
  return goals.map(goal => {
    if (goal.id === goalId) {
      return updatedGoal;
    }
    if (goal.children) {
      return {
        ...goal,
        children: updateGoalInTree(goal.children, goalId, updatedGoal)
      };
    }
    return goal;
  });
}

// Helper function to remove a goal from the tree structure
function removeGoalFromTree(goals: Goal[], goalId: string): Goal[] {
  return goals.filter(goal => {
    if (goal.id === goalId) {
      return false;
    }
    if (goal.children) {
      goal.children = removeGoalFromTree(goal.children, goalId);
    }
    return true;
  });
}